import { Switch } from "@getgreenline/blaze-ui";
import { cn } from "@getgreenline/blaze-ui";
import { ChevronDown } from "lucide-react";
import { type PlacementChannelOption } from "@/hooks/use-placement-catalog";

type PlacementChannelSectionProps = {
  channel: PlacementChannelOption;
  enabled: boolean;
  selectedZoneIds: string[];
  disabled?: boolean;
  onEnabledChange: (enabled: boolean) => void;
  onZoneToggle: (zoneId: string, checked: boolean) => void;
};

export function PlacementChannelSection({
  channel,
  enabled,
  selectedZoneIds,
  disabled = false,
  onEnabledChange,
  onZoneToggle,
}: PlacementChannelSectionProps) {
  const Icon = channel.icon;

  return (
    <div
      className={cn(
        "tw:overflow-hidden tw:rounded-2xl tw:border tw:border-border tw:bg-background",
        disabled && "tw:opacity-55",
      )}
    >
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:bg-sky-50/60 tw:dark:bg-muted tw:px-4 tw:py-3">
        <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-3">
          <Switch
            checked={enabled}
            disabled={disabled}
            onCheckedChange={onEnabledChange}
            aria-label={`Enable ${channel.title}`}
            className={cn(
              "tw:origin-left tw:border-0 tw:shadow-none tw:data-[state=unchecked]:bg-muted-foreground/25",
              !disabled && "tw:cursor-pointer",
            )}
          />
          <div
            className={cn(
              "tw:flex tw:h-7 tw:w-7 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-md",
              channel.accent,
            )}
          >
            <Icon className="tw:h-4 tw:w-4" />
          </div>
          <div className="tw:min-w-0">
            <p className="tw:text-sm tw:font-semibold tw:text-foreground">
              {channel.title}
            </p>
            <p className="tw:text-xs tw:text-muted-foreground">
              {channel.impressionsLabel}
            </p>
          </div>
        </div>
        <div className="tw:flex tw:items-center tw:gap-3">
          <p className="tw:text-sm tw:font-medium tw:text-sky-700">
            {selectedZoneIds.length} / {channel.zones.length}{" "}
            {channel.zonesLabel}
          </p>
          <ChevronDown
            className={cn(
              "tw:h-4 tw:w-4 tw:text-muted-foreground tw:transition-transform",
              enabled && "tw:rotate-180",
            )}
          />
        </div>
      </div>

      {enabled ? (
        <div>
          {channel.zones.map((zone) => {
            const checked = selectedZoneIds.includes(zone.id);

            return (
              <div
                key={zone.id}
                className="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:border-t tw:border-border tw:px-4 tw:py-3"
              >
                <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-3">
                  <Switch
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={(nextChecked) =>
                      onZoneToggle(zone.id, nextChecked)
                    }
                    aria-label={`${channel.title} ${zone.title}`}
                    className={cn(
                      "tw:origin-left tw:border-0 tw:shadow-none tw:data-[state=unchecked]:bg-muted-foreground/25",
                      !disabled && "tw:cursor-pointer",
                    )}
                  />
                  <span className="tw:text-sm tw:font-medium tw:text-foreground">
                    {zone.title}
                  </span>
                </div>
                <span className="tw:text-sm tw:font-medium tw:text-sky-700">
                  {zone.impressionsLabel}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
